"use client";
import { cn } from "@/lib/utils";
import React, { useEffect, useRef } from "react";
import { createNoise3D } from "simplex-noise";

export const WavyBackground = ({
  children,
  className,
  containerClassName,
  colors,
  waveWidth,
  backgroundFill,
  blur = 10,
  speed = "fast",
  waveOpacity = 0.5,
  ...props
}: {
  children?: any;
  className?: string;
  containerClassName?: string;
  colors?: string[];
  waveWidth?: number;
  backgroundFill?: string;
  blur?: number;
  speed?: "slow" | "fast";
  waveOpacity?: number;
  [key: string]: any;
}) => {
  const noise = createNoise3D();
  const canvasRef = useRef<HTMLCanvasElement>(null);
  const dimensionsRef = useRef({ width: 0, height: 0 });
  const animationFrameId = useRef<number>();

  const handleResize = () => {
    if (canvasRef.current && canvasRef.current.parentElement) {
      const { clientWidth, clientHeight } = canvasRef.current.parentElement;
      const dpr = window.devicePixelRatio || 1;
      
      dimensionsRef.current = {
        width: clientWidth * dpr,
        height: clientHeight * dpr
      };

      canvasRef.current.width = clientWidth * dpr;
      canvasRef.current.height = clientHeight * dpr;
      
      const ctx = canvasRef.current.getContext('2d');
      if (ctx) {
        ctx.scale(dpr, dpr);
        ctx.lineCap = 'round';
        ctx.lineJoin = 'round';
      }
    }
  };

  useEffect(() => {
    let canvas = canvasRef.current;
    if (!canvas) return;

    const ctx = canvas.getContext("2d", { alpha: false });
    if (!ctx) return;

    handleResize();
    window.addEventListener('resize', handleResize);

    let nt = 0;
    const waveColors = colors ?? [
      "#38bdf8",
      "#818cf8",
      "#c084fc",
      "#e879f9",
      "#22d3ee",
    ];

    const getSpeed = () => (speed === "slow" ? 0.0005 : 0.001);
    const amplitude = 120; // Wave height
    const baseFrequency = 0.002; // Base frequency for wave oscillation

    const render = () => {
      if (!canvas || !ctx) return;
      
      const { width, height } = dimensionsRef.current;
      
      ctx.fillStyle = backgroundFill || "black";
      ctx.fillRect(0, 0, width, height);
      ctx.globalAlpha = waveOpacity;

      // Draw waves with varying phases and amplitudes
      for (let i = 0; i < 5; i++) {
        ctx.beginPath();
        ctx.lineWidth = (waveWidth || 50) * (1 - i * 0.15); // Gradually decrease wave width
        ctx.strokeStyle = waveColors[i % waveColors.length];

        const phaseOffset = i * Math.PI * 0.5; // Offset each wave's phase
        const waveAmplitude = amplitude * (1 - i * 0.1); // Slightly decrease amplitude for each wave
        const frequency = baseFrequency * (1 + i * 0.2); // Slightly increase frequency for each wave

        // Create smoother wave paths with more points
        for (let x = 0; x <= width; x += 2) {
          const dx = x * frequency;
          const noiseVal = noise(dx, i * 0.3, nt);
          
          // Combine noise with sine wave for more natural movement and position higher
          const y = Math.sin(dx + phaseOffset + nt) * waveAmplitude * 0.5 +
                   noiseVal * waveAmplitude * 0.5;

          if (x === 0) {
            ctx.moveTo(x, y + height * 0.35); // Moved up from 0.5 to 0.35
          } else {
            ctx.lineTo(x, y + height * 0.35); // Moved up from 0.5 to 0.35
          }
        }

        ctx.stroke();
      }

      nt += getSpeed();
      animationFrameId.current = requestAnimationFrame(render);
    };

    render();

    return () => {
      window.removeEventListener('resize', handleResize);
      if (animationFrameId.current) {
        cancelAnimationFrame(animationFrameId.current);
      }
    };
  }, [colors, waveWidth, backgroundFill, blur, speed, waveOpacity]);

  return (
    <div
      className={cn(
        "h-screen flex flex-col items-center justify-center overflow-hidden",
        containerClassName
      )}
    >
      <canvas
        className="absolute inset-0 z-0"
        ref={canvasRef}
        style={{
          filter: `blur(${blur}px)`,
          width: '100%',
          height: '100%',
          objectFit: 'cover',
        }}
      ></canvas>
      <div className={cn("relative z-10", className)} {...props}>
        {children}
      </div>
    </div>
  );
};